<template>
	<v-dialog
		v-model="mostra"
		width="300"
	>
		<v-form
			@submit.prevent="cadastrar"
			ref="form"
		>
			<fieldset :disabled="confirmando">
				<v-card>
					<v-card-text>
						<div class="text-h6 mb-3">
							<template v-if="!cadastro._id"> Cadastre-se para prosseguir </template>
							<template v-else> Confirme suas informações </template>
						</div>
						<v-text-field
							v-if="!cadastro._id"
							ref="primeiroCampo"
							id="cim"
							name="cim"
							label="CIM*"
							v-model="cadastro.cim"
							required
							inputmode="numeric"
							pattern="\d*"
							:rules="[ obrigatorio, erroDeCim]"
							autocomplete="new-password"
							:loading="buscando"
						/>
						<v-text-field
							v-if="!cadastro._id"
							id="email"
							name="email"
							label="E-mail*"
							type="email"
							v-model="cadastro.email"
							:rules="[ obrigatorio, emailValido ]"
							autocomplete="email"
							hint="Você recebe neste e-mail o link para definir sua senha"
						/>
						<v-text-field
							id="nome"
							name="nome"
							label="Nome*"
							v-model="cadastro.nomeCompleto"
							:rules="[ obrigatorio ]"
							autocomplete="new-password"
						/>
						<v-text-field
							label="Loja*"
							v-model="cadastro.loja"
							:rules="[ obrigatorio ]"
							autocomplete="new-password"
						/>
						<SeletorDeGrauDoMacon
							label="Grau*"
							v-model="cadastro.grauMacon"
							:rules="[ obrigatorio ]"
						/>
						<v-text-field
							v-model="cadastro.telefone"
							label="Telefone de Contato"
							v-mask="['(##) ####-####', '(##) #####-####']"
						/>
						<v-text-field
							label="Cargo da Potência"
							v-model="cadastro.cargoDeAdminDaPotencia"
							autocomplete="new-password"
							placeholder="Caso tenha..."
						/>
						<v-text-field
							label="Cargo da Loja"
							v-model="cadastro.cargoDeAdminDaLoja"
							autocomplete="new-password"
							placeholder="Caso tenha..."
						/>
					</v-card-text>
					<v-card-actions class="justify-space-between">
						<v-btn
							color="primary"
							type="submit"
							block
							:disabled="confirmando"
							:loading="confirmando"
						> Confirmar </v-btn>
					</v-card-actions>
				</v-card>
			</fieldset>
		</v-form>
	</v-dialog>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { Cadastro } from 'common/types/Cadastro'
import { emailValido, obrigatorio } from '@/shareds/regras'
import SeletorDeGrauDoMacon from '@/components/SeletorDeGrauDoMacon.vue'
import { mask as vMask } from 'vue-the-mask'
import { IUser } from 'common/types/User'
import axios, { CancelTokenSource } from 'axios'
import IrmaoService from '@/services/IrmaoService'
import { alert, authModule } from '@/store'
import Irmao from 'common/types/Irmao'

const emit = defineEmits<{
	(e: 'confirmado'): void
}>()

const form = ref<HTMLFormElement & {
	validate: () => boolean,
	resetValidation: () => void,
}>()
const primeiroCampo = ref<HTMLInputElement>()
let cancelToken: CancelTokenSource | null = null

const mostra = ref(false)
const erroDeCim = ref<true | string>(true)
const buscando = ref(false)
const confirmando = ref(false)

const cadastro = ref<Cadastro>(criarCadastro())

function mostrar(dadosIniciais: IUser) {
	cadastro.value = dadosIniciais.role !== 'Visitante'
		? irmaoEmCadastro(dadosIniciais)
		: {
			...criarCadastro(),
			potencia: dadosIniciais.idPotencia,
		}
	mostra.value = true
	nextTick(() => {
		form.value!.resetValidation()
		nextTick(() => {
			// o campo de CIM só existe para quem ainda não é irmão
			primeiroCampo.value?.focus()
		})
	})
}

watch(() => cadastro.value.cim, async () => {
	try {
		buscando.value = true
		if (cancelToken) cancelToken.cancel()
		const novoCancelToken = axios.CancelToken.source()
		cancelToken = novoCancelToken
		erroDeCim.value = true
		await IrmaoService.get((cadastro.value.cim).toString(), {
			cancelToken: novoCancelToken.token,
		})
		buscando.value = false
		erroDeCim.value = 'CIM já existe'
	} catch (error) {
		if (axios.isCancel(error)) return
		buscando.value = false
		if (axios.isAxiosError(error) && error.response?.status === 404) return
		// o visitante não pode ver o perfil do irmão, mas o 403 indica que o CIM existe
		if (axios.isAxiosError(error) && error.response?.status === 403) {
			erroDeCim.value = 'CIM já existe'
			return
		}
		erroDeCim.value = 'Não foi possível buscar o CIM'
	}
})

async function cadastrar() {
	if (!form.value!.validate()) return
	try {
		confirmando.value = true
		await authModule.registrar(cadastro.value)
		emit('confirmado')
		mostra.value = false
	} catch (error) {
		alert.setError(error)
	} finally {
		confirmando.value = false
	}
}

defineExpose({ mostrar })

function irmaoEmCadastro(irmao: Irmao): Cadastro {
	return {
		...irmao,
		telefone: irmao.telefones[0]?.valor || '',
		email: irmao.email ?? '',
		potencia: irmao.potencia._id,
		loja: irmao.loja.nome,
	}
}

function criarCadastro(): Cadastro {
	return {
		nomeCompleto: '',
		loja: '',
		grauMacon: 'Aprendiz',
		cim: '',
		telefone: '',
		email: '',
		potencia: '',
		cargoDeAdminDaLoja: '',
		cargoDeAdminDaPotencia: '',
		ultimaAtualizacao: '',
	}
}
</script>
